import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, X } from 'lucide-react';
import { api } from '../../services/api';
import { countryName, formatDate, maskedPhone } from '../format';
import '../crm.css';

const PAGE_SIZE = 50;

function NewClientForm({ onClose, onCreated }) {
  const { t } = useTranslation('common');
  const [form, setForm] = useState({ name: '', phone: '', email: '', company: '', city: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const created = await api.createCrmClient({
        name: form.name.trim(),
        phones: form.phone.trim() ? [form.phone.trim()] : [],
        emails: form.email.trim() ? [form.email.trim()] : [],
        company: form.company.trim(),
        city: form.city.trim(),
      });
      await onCreated(created);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail?.problem ? t(`crm.phoneProblem.${detail.problem}`) : String(detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="crm-card" onSubmit={submit}>
      <div className="crm-card-head">
        <h2>{t('crm.clients.addTitle')}</h2>
        <button type="button" className="crm-icon-btn" onClick={onClose} aria-label={t('crm.close')}>
          <X size={16} />
        </button>
      </div>
      <div className="crm-field-grid">
        <label className="crm-field">
          {t('crm.manual.name')}
          <input className="crm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={120} />
        </label>
        <label className="crm-field">
          {t('crm.manual.phone')}
          <input className="crm-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+380 67 123 45 67" maxLength={25} />
        </label>
        <label className="crm-field">
          {t('crm.manual.email')}
          <input className="crm-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} maxLength={200} />
        </label>
        <label className="crm-field">
          {t('crm.manual.company')}
          <input className="crm-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} maxLength={200} />
        </label>
        <label className="crm-field">
          {t('crm.client.city')}
          <input className="crm-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} maxLength={120} />
        </label>
      </div>
      {error && <div className="crm-error">{error}</div>}
      <div className="crm-card-actions">
        <button type="submit" className="crm-btn primary" disabled={saving || !form.name.trim()}>
          {t('crm.clients.save')}
        </button>
      </div>
    </form>
  );
}

export default function CrmClientsPage() {
  const { t, i18n } = useTranslation('common');
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState(params.get('search') || '');
  const page = Number(params.get('page') || 0);

  const load = useCallback(async () => {
    setError('');
    try {
      setData(
        await api.listCrmClients({
          search: params.get('search') || undefined,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        }),
      );
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }, [params, page]);

  useEffect(() => {
    load();
  }, [load]);

  const submitSearch = (event) => {
    event.preventDefault();
    const patch = new URLSearchParams(params);
    if (search.trim()) patch.set('search', search.trim());
    else patch.delete('search');
    patch.delete('page');
    setParams(patch);
  };

  return (
    <div className="crm-page">
      <header className="crm-head">
        <div>
          <h1 className="crm-title">{t('crm.clients.title')}</h1>
          <p className="crm-sub">{t('crm.clients.subtitle')}</p>
        </div>
        <button type="button" className="crm-btn primary" onClick={() => setAdding(true)}>
          <Plus size={15} />
          {t('crm.clients.add')}
        </button>
      </header>

      {error && <div className="crm-error">{String(error)}</div>}

      <form className="crm-searchbar" onSubmit={submitSearch}>
        <Search size={15} />
        <input
          className="crm-search-input"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('crm.clients.searchPlaceholder')}
        />
        <button type="submit" className="crm-btn">{t('crm.search')}</button>
      </form>

      {adding && (
        <NewClientForm
          onClose={() => setAdding(false)}
          onCreated={async () => {
            setAdding(false);
            await load();
          }}
        />
      )}

      {data && data.clients.length === 0 && <p className="crm-empty">{t('crm.clients.empty')}</p>}

      {data && data.clients.length > 0 && (
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                <th>{t('crm.client.name')}</th>
                <th>{t('crm.client.contacts')}</th>
                <th>{t('crm.client.where')}</th>
                <th>{t('crm.client.translations')}</th>
                <th>{t('crm.client.updated')}</th>
              </tr>
            </thead>
            <tbody>
              {data.clients.map((client) => (
                <tr key={client.id}>
                  <td>
                    <Link className="crm-link" to={`/crm/clients/${client.id}`}>{client.name}</Link>
                    {client.company && <div className="crm-cell-sub">{client.company}</div>}
                  </td>
                  <td>
                    {client.phones.map((phone) => (
                      <div key={phone.e164}>{maskedPhone(phone)}</div>
                    ))}
                    {client.emails.map((email) => (
                      <div key={email} className="crm-cell-sub">{email}</div>
                    ))}
                  </td>
                  <td>{[client.city, countryName(client.region, i18n.language)].filter(Boolean).join(', ') || '—'}</td>
                  <td>{client.document_ids.length}</td>
                  <td>{formatDate(client.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
